import React, { useState } from "react";
import {
  Image,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
  Alert,
  ActivityIndicator,
} from "react-native";
import { Pencil, Plus, Trash } from "@phosphor-icons/react";

import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import {
  useGetNotesForStudentQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
} from "../../../graphql/generated/graphql";

interface Note {
  id: string;
  content: string;
  created: string;
  lastModified: string;
  author: {
    id: string;
    name: string;
    profilePicture?: string;
  };
  subject: {
    id: string;
    name: string;
  };
}

interface Student {
  id: string;
}

interface InternalNotesTabProps {
  student: Student;
}

const InternalNotesTab: React.FC<InternalNotesTabProps> = ({ student }) => {
  const [newNoteText, setNewNoteText] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");

  // GraphQL hooks
  const { data, loading, error, refetch } = useGetNotesForStudentQuery({
    variables: { studentId: student.id },
    skip: !student.id,
  });

  const [createNoteMutation] = useCreateNoteMutation({
    onCompleted: () => {
      setNewNoteText("");
      refetch();
    },
    onError: (error) => {
      Alert.alert("Error", error.message);
    },
  });

  const [updateNoteMutation] = useUpdateNoteMutation({
    onCompleted: () => {
      setEditingNoteId(null);
      setEditingText("");
      refetch();
    },
    onError: (error) => {
      Alert.alert("Error", error.message);
    },
  });

  const [deleteNoteMutation] = useDeleteNoteMutation({
    onCompleted: (data) => {
      console.log("Deleted note:", data);
      refetch();
    },
    onError: (error) => {
      console.log("Delete note error:", error);
      Alert.alert("Error", error.message);
    },
  });

  console.log("deleteNoteMutation hook created:", deleteNoteMutation);

  const notes: Note[] = data?.getNotesForStudent?.data || [];

  const filteredNotes = notes.filter(
    (note) =>
      note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.author.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addNote = async () => {
    if (!newNoteText.trim()) return;

    try {
      await createNoteMutation({
        variables: {
          input: {
            studentId: student.id,
            content: newNoteText.trim(),
          },
        },
      });
    } catch (error) {
      console.error("Error creating note:", error);
    }
  };

  const deleteNote = async (noteId: string) => {
    try {
      const result = await deleteNoteMutation({
        variables: { noteId },
      });
    } catch (error) {
      console.error("Mutation error:", error);
    }
  };

  const startEditNote = (note: Note) => {
    setEditingNoteId(note.id);
    setEditingText(note.content);
  };

  const saveEditNote = async () => {
    if (!editingNoteId || !editingText.trim()) return;

    try {
      await updateNoteMutation({
        variables: {
          noteId: editingNoteId,
          input: {
            content: editingText.trim(),
          },
        },
      });
    } catch (error) {
      console.error("Error updating note:", error);
    }
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditingText("");
  };

  const getAuthorInitials = (name: string) => {
    return name
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color="#0000ff" />
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <CustomText text={`Error loading notes: ${error.message}`} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.sectionHeader}>
        <CustomText text="internal_notes" size="l" weight="bold" />
        <View style={styles.searchContainer}>
          <TextInput
            style={styles.searchInput}
            placeholder="Search notes..."
            value={searchTerm}
            onChangeText={setSearchTerm}
            placeholderTextColor="#9CA3AF"
          />
        </View>
      </View>

      {/* Add Note Form */}
      <View style={styles.addNoteForm}>
        <TextInput
          style={styles.noteTextarea}
          placeholder="Add an internal note about this student..."
          value={newNoteText}
          onChangeText={setNewNoteText}
          multiline
          numberOfLines={3}
          placeholderTextColor="#9CA3AF"
        />
        <View style={styles.formActions}>
          <CustomButton
            text="Add Note"
            onPress={addNote}
            style={styles.addNoteButton}
            textStyle={styles.addNoteButtonText}
            icon={<Plus size={16} color="#FFFFFF" />}
            bold={true}
          />
        </View>
      </View>

      {/* Notes List */}
      <ScrollView style={styles.notesList} showsVerticalScrollIndicator={false}>
        {filteredNotes.map((note) => (
          <View key={note.id} style={styles.noteItem}>
            <View style={styles.noteHeader}>
              <View style={styles.noteAuthor}>
                <View style={styles.authorAvatar}>
                  {note.author.profilePicture ? (
                    <Image source={{ uri: note.author.profilePicture }} style={styles.avatarImage} />
                  ) : (
                    <View style={styles.avatarPlaceholder}>
                      <CustomText
                        text={getAuthorInitials(note.author.name)}
                        style={styles.avatarInitials}
                        useTranslationText={false}
                      />
                    </View>
                  )}
                </View>
                <View style={styles.authorInfo}>
                  <CustomText
                    text={note.author.name}
                    weight="medium"
                    style={styles.authorName}
                    useTranslationText={false}
                  />
                  <CustomText
                    text={formatTimestamp(note.created)}
                    size="xs"
                    style={styles.noteTimestamp}
                    useTranslationText={false}
                  />
                </View>
              </View>
              <View style={styles.noteActions}>
                <TouchableOpacity style={styles.actionButton} onPress={() => startEditNote(note)}>
                  <Pencil size={16} color="#6B7280" />
                </TouchableOpacity>
                <TouchableOpacity style={styles.actionButton} onPress={() => deleteNote(note.id)}>
                  <Trash size={16} color="#6B7280" />
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.noteContent}>
              {editingNoteId === note.id ? (
                <View>
                  <TextInput
                    style={styles.editTextarea}
                    value={editingText}
                    onChangeText={setEditingText}
                    multiline
                    numberOfLines={3}
                  />
                  <View style={styles.editActions}>
                    <CustomButton
                      text="Save"
                      onPress={saveEditNote}
                      style={styles.saveButton}
                      textStyle={styles.saveButtonText}
                    />
                    <CustomButton
                      text="Cancel"
                      onPress={cancelEditNote}
                      style={styles.cancelButton}
                      textStyle={styles.cancelButtonText}
                    />
                  </View>
                </View>
              ) : (
                <CustomText text={note.content} size="m" style={styles.noteText} useTranslationText={false} />
              )}
            </View>
          </View>
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 12,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 32,
  },
  searchContainer: {
    flex: 1,
    maxWidth: 300,
    marginLeft: 16,
  },
  searchInput: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: "#374151",
  },
  addNoteForm: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
  },
  noteTextarea: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: "#374151",
    minHeight: 80,
    textAlignVertical: "top",
  },
  formActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 16,
  },
  addNoteButton: {
    backgroundColor: "#3E68FE",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  addNoteButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "500",
  },
  notesList: {
    flex: 1,
  },
  noteItem: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  noteHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  noteAuthor: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  authorAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: "#E5E7EB",
    marginRight: 12,
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarImage: {
    width: "100%",
    height: "100%",
  },
  avatarPlaceholder: {
    width: "100%",
    height: "100%",
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarInitials: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  authorInfo: {
    flex: 1,
  },
  authorName: {
    color: "#111827",
    fontSize: 14,
    fontWeight: "500",
  },
  noteTimestamp: {
    color: "#6B7280",
    fontSize: 12,
    marginTop: 2,
  },
  noteActions: {
    flexDirection: "row",
    gap: 4,
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
  },
  noteContent: {
    marginLeft: 52,
  },
  noteText: {
    color: "#374151",
    lineHeight: 20,
    fontSize: 14,
  },
  editTextarea: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: "#374151",
    minHeight: 80,
    textAlignVertical: "top",
    marginBottom: 12,
  },
  editActions: {
    flexDirection: "row",
    gap: 8,
  },
  saveButton: {
    backgroundColor: "#3E68FE",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  saveButtonText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "500",
  },
  cancelButton: {
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  cancelButtonText: {
    color: "#6B7280",
    fontSize: 12,
    fontWeight: "500",
  },
});

export default InternalNotesTab;
